import React, { useState, useContext, useEffect, useRef } from "react";
import TitleBar from "./TitleBar";
import JBLogo from "../../img/logo_JB.png";
import { useNavigate } from "react-router-dom";
import {
  login_user,
  fetch_session,
  fetchActiveAcademicSession,
  getStudentProfile,
} from "../../controllers/loginRoutes";
import LoadingContext from "../../contexts/LoadingContext.js";
import { useAuth } from "../../contexts/AuthContext.jsx";
import { normalizeParentLoginInput } from "../../utils/parentLoginId.js";
import {
  clearAuthToken,
  clearActiveAcademicYear,
  clearActiveStudentId,
  clearPortalSessionStorage,
  resolveStudentIdCandidate,
  clearParentBootstrapCache,
  writeAuthToken,
  writeActiveAcademicYear,
  writeActiveStudentId,
} from "../../utils/parentPortalAuth.js";
import {
  resolveDashboardRoute,
  resolveSessionRoles,
} from "../../utils/rbac.js";

const SESSION_TIMEOUT_NOTICE_KEY = "erp_session_timeout_notice";
const LOGIN_FAILURE_MESSAGE = "Invalid login ID or password.";
const SESSION_FAILURE_MESSAGE = "System setup in progress.";
const SESSION_EXPIRED_MESSAGE = "Session expired. Please login again.";
const ROLE_MISSING_MESSAGE =
  "Login succeeded but role is not assigned. Ask admin to activate your account.";

const resolveSessionDataObject = (payload = {}) =>
  payload?.data && typeof payload.data === "object" ? payload.data : {};

const resolveSessionRoleSource = (payload = {}) => {
  const nested = resolveSessionDataObject(payload);
  return {
    role: payload?.role || nested?.role || null,
    admin_data:
      payload?.admin_data ||
      nested?.admin_data ||
      payload?.admin ||
      nested?.admin ||
      null,
    teacher_data:
      payload?.teacher_data ||
      nested?.teacher_data ||
      payload?.teacher ||
      nested?.teacher ||
      null,
    student_data:
      payload?.student_data ||
      nested?.student_data ||
      payload?.student ||
      nested?.student ||
      null,
    parent_data:
      payload?.parent_data ||
      nested?.parent_data ||
      payload?.parent ||
      nested?.parent ||
      null,
    user: payload?.user || nested?.user || null,
  };
};

const Login = () => {
  const { toggleLoading } = useContext(LoadingContext);
  const { loading: authLoading, token, systemRole, managementRole } = useAuth();
  const [credentials, setCredentials] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const loginInProgressRef = useRef(false);

  useEffect(() => {
    const timeoutNotice = localStorage.getItem(SESSION_TIMEOUT_NOTICE_KEY);
    if (timeoutNotice) {
      setCredentials(`*${timeoutNotice}`);
      localStorage.removeItem(SESSION_TIMEOUT_NOTICE_KEY);
    }
  }, []);

  useEffect(() => {
    if (loginInProgressRef.current || authLoading || !token) return;
    const route = resolveDashboardRoute({ systemRole, managementRole });
    if (route && route !== "/login") {
      navigate(route, { replace: true });
    }
  }, [authLoading, token, systemRole, managementRole, navigate]);

  const handleUsernameChange = (event) => {
    setUsername(event.target.value);
    if (credentials) setCredentials("");
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
    if (credentials) setCredentials("");
  };

  const resolveRole = (resp) => {
    const { systemRole } = resolveSessionRoles(resp);
    if (systemRole === "SUPER_ADMIN" || systemRole === "ADMIN")
      return "Management";
    if (systemRole === "TEACHER") return "Teacher";
    if (systemRole === "STUDENT") return "Student";
    return null;
  };

  const normalizeRoleToken = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "");

  const ADMIN_ROLE_TOKENS = new Set([
    "admin",
    "superadmin",
    "director",
    "principal",
    "headmaster",
    "headteacher",
    "vp",
    "viceprincipal",
    "coordinator",
    "reception",
    "receptionist",
    "management",
  ]);

  const TEACHER_ROLE_TOKENS = new Set(["teacher", "staff", "faculty"]);
  const PARENT_ROLE_TOKENS = new Set(["parent", "student"]);

  const normalizeRole = (role) => {
    if (!role) return null;
    const token = normalizeRoleToken(role);
    if (ADMIN_ROLE_TOKENS.has(token)) return "Management";
    if (TEACHER_ROLE_TOKENS.has(token) || token.includes("teacher"))
      return "Teacher";
    if (PARENT_ROLE_TOKENS.has(token)) return "Student";
    return null;
  };

  const resolveStudentIdFromPayload = (payload) =>
    resolveStudentIdCandidate(
      payload?.studentId,
      payload?.student_id,
      payload?.activeStudentId,
      payload?._id,
      payload?.id,
      payload?.ID,
      payload?.erpId,
      payload?.erpID,
      payload?.login_id,
      payload?.loginId,
      payload?.rollno,
      payload?.user?.studentId,
      payload?.user?.student_id,
      payload?.user?.activeStudentId,
      payload?.user?._id,
      payload?.user?.id,
      payload?.user?.ID,
      payload?.user?.erpId,
      payload?.user?.erpID,
      payload?.user?.userId,
      payload?.user?.username,
      payload?.user?.email,
      payload?.user?.login_id,
      payload?.user?.loginId,
    );

  const resolveTeacherIdFromPayload = (payload) =>
    resolveStudentIdCandidate(
      payload?.teacherUserId,
      payload?.teacher_user_id,
      payload?.id,
      payload?.userId,
      payload?.user_id,
      payload?.user?.teacherUserId,
      payload?.user?.teacher_user_id,
      payload?.user?.id,
      payload?.user?.userId,
      payload?.user?.user_id,
    );

  const resolveAcademicYearFromPayload = (payload) =>
    String(
      payload?.label ??
        payload?.academicYear ??
        payload?.academic_year ??
        payload?.session ??
        payload?.academic_session ??
        payload?.current_session ??
        payload?.user?.academicYear ??
        payload?.user?.academic_year ??
        payload?.user?.session ??
        payload?.user?.academic_session ??
        payload?.user?.current_session ??
        "",
    ).trim();

  const resolveAcademicYearFromActiveSession = (payload) =>
    resolveAcademicYearFromPayload(payload?.session) ||
    resolveAcademicYearFromPayload(payload);

  const isSessionOk = (payload) => {
    const source = resolveSessionRoleSource(payload);
    const hasSessionIdentity = Boolean(
      source.admin_data ||
      source.teacher_data ||
      source.student_data ||
      source.parent_data ||
      source.role ||
      source.user,
    );
    if (hasSessionIdentity) return true;
    return false;
  };

  const resolveSessionFailureMessage = (payload) => {
    const status = Number(payload?.status || payload?._httpStatus || 0);
    const backendMessage = String(
      payload?.message || payload?.error || "",
    ).trim();
    const hasAcademicSessionShape =
      Boolean(payload?.session) &&
      !payload?.role &&
      !payload?.admin_data &&
      !payload?.teacher_data;

    if (status === 401 || status === 403) {
      return SESSION_EXPIRED_MESSAGE;
    }
    if (backendMessage) {
      return backendMessage;
    }
    if (hasAcademicSessionShape) {
      return "Login succeeded, but user session mapping is misconfigured. Please contact support.";
    }
    return ROLE_MISSING_MESSAGE;
  };

  const handleLogin = async (event) => {
    if (event) event.preventDefault();
    if (loginInProgressRef.current) return;
    const schoolIssuedId = username.trim();
    if (!schoolIssuedId || !password) {
      setCredentials("*Enter login ID and password.");
      return;
    }
    loginInProgressRef.current = true;
    setSubmitting(true);
    clearParentBootstrapCache();
    toggleLoading(true);
    try {
      const normalizedLogin = normalizeParentLoginInput(schoolIssuedId);
      const loginCandidates = [];
      if (schoolIssuedId) loginCandidates.push(schoolIssuedId);
      if (normalizedLogin && normalizedLogin !== schoolIssuedId) {
        loginCandidates.push(normalizedLogin);
      }

      let loginResp = null;
      let lastError = null;
      for (let index = 0; index < loginCandidates.length; index += 1) {
        const candidate = loginCandidates[index];
        try {
          loginResp = await login_user({
            username: candidate,
            loginId: candidate,
            password,
          });
          break;
        } catch (error) {
          lastError = error;
          const status = Number(error?.status || 0);
          const shouldRetry =
            status === 401 && index < loginCandidates.length - 1;
          if (shouldRetry) continue;
          throw error;
        }
      }

      if (!loginResp && lastError) {
        throw lastError;
      }
      const authToken = String(loginResp?.token || "").trim();
      if (!authToken || authToken === "[object Object]") {
        const backendLoginMessage = String(
          loginResp?.errors?.[0]?.msg ||
            loginResp?.errors?.[0]?.message ||
            loginResp?.message ||
            loginResp?.error ||
            "",
        ).trim();
        setCredentials(`*${backendLoginMessage || LOGIN_FAILURE_MESSAGE}`);
        setUsername("");
        setPassword("");
        return;
      }

      localStorage.removeItem("data");
      clearActiveStudentId();
      clearActiveAcademicYear();
      localStorage.removeItem(SESSION_TIMEOUT_NOTICE_KEY);
      writeAuthToken(authToken);

      const sessionResp = await fetch_session();
      const sessionOk = isSessionOk(sessionResp);
      const role = normalizeRole(resolveRole(sessionResp));
      if (!sessionOk || !role) {
        const message = resolveSessionFailureMessage(sessionResp);
        setCredentials(`*${message}`);
        clearAuthToken();
        localStorage.removeItem("data");
        clearActiveStudentId();
        clearActiveAcademicYear();
        return;
      }

      switch (role) {
        case "Management": {
          clearActiveStudentId();
          clearActiveAcademicYear();
          const { systemRole, managementRole } =
            resolveSessionRoles(sessionResp);
          const route = resolveDashboardRoute({
            systemRole,
            managementRole,
          });
          navigate(route || "/login", { replace: true });
          break;
        }
        case "Student": {
          const sessionSource = resolveSessionRoleSource(sessionResp);
          const communicationHistoryOnly = Boolean(
            sessionResp?.communicationHistoryOnly ||
            sessionSource.user?.communicationHistoryOnly,
          );
          let activeStudentId =
            resolveStudentIdFromPayload(sessionSource.student_data) ||
            resolveStudentIdFromPayload(sessionSource.parent_data) ||
            resolveStudentIdFromPayload(sessionSource.user);
          if (!activeStudentId) {
            setCredentials(`*${LOGIN_FAILURE_MESSAGE}`);
            clearAuthToken();
            return;
          }
          if (communicationHistoryOnly) {
            writeActiveStudentId(activeStudentId);
            clearActiveAcademicYear();
            navigate("/parent/messages", { replace: true });
            break;
          }
          let activeSessionResp = null;
          let activeAcademicYear = "";
          try {
            activeSessionResp = await fetchActiveAcademicSession();
            activeAcademicYear =
              resolveAcademicYearFromActiveSession(activeSessionResp);
          } catch (error) {
            activeAcademicYear = "";
          }
          if (!activeAcademicYear) {
            setCredentials(`*${SESSION_FAILURE_MESSAGE}`);
            clearAuthToken();
            return;
          }
          try {
            const profile = await getStudentProfile(activeStudentId, {
              suppressGlobalError: true,
              allowNotFound: true,
            });
            if (profile && typeof profile === "object") {
              const canonicalId = resolveStudentIdFromPayload(profile);
              if (canonicalId && canonicalId !== activeStudentId) {
                activeStudentId = canonicalId;
              }
            }
          } catch (error) {
            // ignore profile fetch failures
          }
          writeActiveStudentId(activeStudentId);
          writeActiveAcademicYear(activeAcademicYear);
          navigate("/parent/home", { replace: true });
          break;
        }
        case "Teacher":
          clearActiveStudentId();
          clearActiveAcademicYear();
          {
            const sessionSource = resolveSessionRoleSource(sessionResp);
            const teacherId =
              resolveTeacherIdFromPayload(sessionSource.teacher_data) ||
              resolveTeacherIdFromPayload(sessionSource.user);
            if (!teacherId) {
              setCredentials(`*${SESSION_FAILURE_MESSAGE}`);
              clearPortalSessionStorage();
              return;
            }
          }
          navigate("/teacher/home", { replace: true });
          break;
        default:
          setCredentials(`*${LOGIN_FAILURE_MESSAGE}`);
          break;
      }
    } catch (error) {
      const message = String(error?.message || "").trim();
      const status = Number(error?.status || error?.payload?._httpStatus || 0);
      const isSessionError =
        message.toLowerCase().includes("session") ||
        message.toLowerCase().includes("token") ||
        message.toLowerCase().includes("unauthorized");
      const shouldShowBackendMessage =
        isSessionError ||
        status >= 500 ||
        status === 429 ||
        status === 403 ||
        status === 400 ||
        status === 404 ||
        status === 0;
      const fallbackMessage =
        status >= 500
          ? "Login service is temporarily unavailable. Please try again."
          : LOGIN_FAILURE_MESSAGE;
      setCredentials(
        `*${shouldShowBackendMessage ? message || fallbackMessage : LOGIN_FAILURE_MESSAGE}`,
      );
      clearAuthToken();
      localStorage.removeItem("data");
      clearActiveStudentId();
      clearActiveAcademicYear();
      clearParentBootstrapCache();
    } finally {
      loginInProgressRef.current = false;
      setSubmitting(false);
      toggleLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-[#F4F6F8]">
      <TitleBar />
      <div
        data-testid="login-shell"
        className="mx-auto flex w-full max-w-6xl flex-1 flex-col items-center justify-start gap-4 px-4 pb-4 pt-3 sm:gap-6 sm:py-8 lg:flex-row lg:justify-center lg:gap-8 lg:py-10"
      >
        <div className="hidden w-full max-w-md lg:block">
          <div className="flex h-full w-full items-center justify-center rounded-lg border border-[#F4F6F8] bg-white p-6 shadow-sm sm:p-10">
            <img
              src={JBLogo}
              alt="JB School logo"
              className="max-h-56 w-auto object-contain sm:max-h-80"
            />
          </div>
        </div>

        <div className="w-full max-w-md">
          <div
            data-testid="login-card"
            className="relative overflow-hidden rounded-lg border border-[#F4F6F8] bg-white p-4 text-left shadow-sm sm:p-8"
          >
            <img
              src={JBLogo}
              alt=""
              aria-hidden="true"
              data-testid="login-watermark"
              className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 object-contain opacity-[0.08] sm:-right-10 sm:-top-10 sm:h-44 sm:w-44 sm:opacity-10 lg:hidden"
            />
            <div className="relative z-10">
              <h1 className="text-xl font-semibold text-[#111111] sm:text-2xl">
                Welcome back
              </h1>
              <p className="mt-1 text-xs text-[#111111] sm:text-sm">
                Sign in with your login ID and password.
              </p>
              <div
                className="mt-2 h-1 w-10 rounded-full bg-[#F5C400] sm:mt-3 sm:w-12"
                aria-hidden="true"
              />

              <form
                onSubmit={handleLogin}
                className="mt-4 space-y-3 sm:mt-6 sm:space-y-4"
              >
                <div className="space-y-1">
                  <label
                    htmlFor="loginId"
                    className="text-sm font-medium text-[#111111]"
                  >
                    Login ID
                  </label>
                  <input
                    id="loginId"
                    className="w-full rounded-lg border border-[#F4F6F8] bg-white px-3 py-2 text-[#111111] shadow-sm placeholder:text-[#111111] focus:border-[#0B4DB8] focus:outline-none focus:ring-2 focus:ring-[#F4F6F8] sm:py-2.5"
                    placeholder="e.g., JBS25PN03"
                    value={username}
                    onChange={handleUsernameChange}
                    autoComplete="username"
                  />
                </div>

                <div className="space-y-1">
                  <label
                    htmlFor="loginPassword"
                    className="text-sm font-medium text-[#111111]"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="loginPassword"
                      className="w-full rounded-lg border border-[#F4F6F8] bg-white px-3 py-2 pr-16 text-[#111111] shadow-sm placeholder:text-[#111111] focus:border-[#0B4DB8] focus:outline-none focus:ring-2 focus:ring-[#F4F6F8] sm:py-2.5"
                      placeholder="Enter your password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={handlePasswordChange}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      aria-pressed={showPassword}
                      className="absolute inset-y-0 right-0 min-h-11 px-3 text-sm font-semibold text-[#0B4DB8] focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#0B4DB8]"
                      onClick={() => setShowPassword((visible) => !visible)}
                    >
                      {showPassword ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>

                {credentials && (
                  <p className="text-sm text-red-600" aria-live="polite">
                    {credentials}
                  </p>
                )}

                <button
                  type="submit"
                  className="w-full rounded-lg bg-[#0B4DB8] py-2.5 text-base font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={!username || !password || submitting}
                >
                  {submitting ? "Signing in..." : "Log in"}
                </button>

                <div className="flex flex-wrap items-center justify-between gap-2 pt-1 text-sm">
                  <a href="/forget" className="font-medium text-[#0B4DB8]">
                    Forgot password
                  </a>
                  <a
                    href="/admissions/apply"
                    className="font-medium text-[#0B4DB8]"
                  >
                    Apply for Admission
                  </a>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Login;
